'use client';

import './desktop.scss';
import { ReactNode, useEffect } from "react";
import HeaderDesktop from "./HeaderDesktop";
import FooterDesktop from "./FooterDesktop";
import { useContentSize, useSetContentSize } from "@/components/SizeProvider";
import SiderRight from "./SiderRight";
import SiderLeft from "./SiderLeft";
import { ProjectModel } from "@/lib/models/Project";
import { calculateContentSize } from "@/lib/utils/contentSize";

interface Props {
  children: ReactNode;
  projects: ProjectModel[];
}

export default function Desktop({ children, projects }: Props) {
  const contentSize = useContentSize();
  const setContentSize = useSetContentSize();

  useEffect(() => {
    const syncContentSize = () => {
      setContentSize(calculateContentSize());
    };

    syncContentSize();
    window.addEventListener("resize", syncContentSize);

    return () => {
      window.removeEventListener("resize", syncContentSize);
    }
  }, [setContentSize]);

  const isMeasured = contentSize.width > 0 && contentSize.height > 0;

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      animation: isMeasured ? 'fadeInContent 300ms ease-in-out' : 'none',
      visibility: isMeasured ? 'visible' : 'hidden',
    }}>
      {/* Noise background */}
      <div className="noise-container" />

      {/* Fixed element with size: (contentSize.width, contentSize.height), positioned at center */}
      <div 
        className="layout-desktop-container"
        id="desktop-content"
        style={{
          width: contentSize.width,
          height: contentSize.height,
        }}
      >
        <HeaderDesktop/>
        <SiderLeft />
        <SiderRight projects={projects} />
        <FooterDesktop/>
      </div>

      {/*
        Putting page content outside of layout-desktop-container allows us to utilize the full viewport.
        However, if you need to position content within the area marked out by header, footer, siderLeft and siderRight,
        you'll have to manually compute the available space, or refactor as necessary.
      */}
      {children}
    </div>
  );
};
